import React, { useState, useEffect, useMemo, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { getStandardDomains, getPremiumDomains, MilestoneState } from '../../types/profile';
import { MilestoneWithText } from '../../types/milestones';
import { appConfig } from '../../config/configManager';
import { getColor } from '../../config/appConfig';
import { createMasterMilestoneStorage } from '../../storage/masterMilestoneStorage';
import { useProfileStore, useContentStore, useUserStore } from '../../contexts/StoreContext';
import { formatPhysicalAge, formatDevAge } from '../../utils/ageFormatting';
import { calculateDevFloorCeiling, updateProfileDevFloorCeiling, getMilestoneRange, mapDomainKey, mapDomainToAdminNodeName } from '../../utils/devFloorCeiling';
import { computeDevelopmentalAgeOptimized, computeDevelopmentalAge, DevelopmentalAgeInput, DevelopmentalAgeResult } from '../../utils/developmentalAge';
import { AdminSegmentStorage } from '../../services/AdminSegmentStorage';
import { createcontentService } from '../../services/ContentService';
import { logContentStoreDebugInfo, hasAdminSegment, getAdminSegmentInfo } from '../../utils/debugContentStore';
import { calculateAgeInMonths } from '../../types/profile';
import { QuickAccessTable, toMemoryOptimized } from '../../utils/tableAccess';
import { checkDomainStaleness, loadStalenessThreshold, loadShowLastUpdateSetting, formatComputationTimestamp } from '../../utils/milestoneStaleness';
import type { ContentRecord } from '../../stores/contentStore';

// Create a shared storage instance using the global persistent storage
let sharedMilestoneStorage: ReturnType<typeof createMasterMilestoneStorage> | null = null;
let lastInvalidationTime = 0;

const getRuntimeStorage = () =>
  (globalThis as any).__proGroupsStorage ?? (globalThis as any).__childNavStorage ?? null;

const getSharedStorage = () => {
  // Initialize timestamp tracking if not set
  if ((globalThis as any).__milestoneLastChecked === undefined) {
    (globalThis as any).__milestoneLastChecked = Date.now();
  }
  if ((globalThis as any).__milestoneStorageInvalidated === undefined) {
    (globalThis as any).__milestoneStorageInvalidated = 0;
  }
  
  // Always check if storage has been invalidated - force refresh if needed
  const currentInvalidationTime = (globalThis as any).__milestoneStorageInvalidated || 0;
  
  // Only create new storage if it doesn't exist OR if there's a genuine invalidation signal
  if (!sharedMilestoneStorage || (currentInvalidationTime > 0 && currentInvalidationTime !== lastInvalidationTime)) {
    // Create/recreate storage instance when invalidated or missing
    const globalStorage = getRuntimeStorage();
    if (globalStorage) {
      if (currentInvalidationTime !== lastInvalidationTime) {
        console.log(`🔄 Creating fresh milestone storage instance (invalidation: ${currentInvalidationTime} !== ${lastInvalidationTime})`);
      }
      console.log('🔍 STORAGE REFRESH: Forcing fresh milestone storage instance');
      console.log('  - Global storage available:', !!globalStorage);
      console.log('  - Global storage type:', typeof globalStorage);
      console.log('  - Global storage constructor:', globalStorage ? globalStorage.constructor.name : 'null');
      console.log('  - Global storage hash:', globalStorage && globalStorage.toString ? globalStorage.toString().substring(0, 50) : 'no toString');
      
      sharedMilestoneStorage = createMasterMilestoneStorage(globalStorage);
      lastInvalidationTime = currentInvalidationTime;
      
      console.log('🔍 STORAGE DEBUG: Using fresh milestone storage instance');
      console.log('  - Storage instance created:', !!sharedMilestoneStorage);
      
      if (currentInvalidationTime > 0 && currentInvalidationTime !== lastInvalidationTime) {
        console.log('🔄 MilestonesView: Storage instance refreshed due to invalidation signal');
      }
    } else {
      console.warn('⚠️ Global storage not available in MilestonesView');
      throw new Error('Global storage not available');
    }
  }
  return sharedMilestoneStorage!;
};

// Export the shared storage access function for use by other components
export const getSharedMilestoneStorage = getSharedStorage;

// Helper function to check if AdminSegments are available in storage
const checkForAdminSegments = async (storage: any): Promise<boolean> => {
  try {
    // Check for AdminSegment storage keys (pattern: admin_nodes_segment_*)
    const segmentKeys = ['admin_nodes_segment_11255', 'admin_nodes_segment_11270'];
    
    for (const key of segmentKeys) {
      const data = await storage.getItem(key);
      if (data) {
        console.log(`✅ Found AdminSegment data at key: ${key}`);
        return true;
      }
    }
    
    console.log('⚠️ No AdminSegment data found in storage');
    return false;
  } catch (error) {
    console.warn('⚠️ Error checking for AdminSegments:', error);
    return false;
  }
};

interface MilestonesViewProps {
  selectedDomain?: string | null;
  onDomainSelected?: () => void;
  showBackToSnapshot?: boolean;
  onBackToSnapshot?: () => void;
  onUpdateConcerns?: () => void;
}

export const MilestonesView: React.FC<MilestonesViewProps> = ({ selectedDomain: propSelectedDomain, onDomainSelected, showBackToSnapshot, onBackToSnapshot, onUpdateConcerns }) => {
  // Get configuration and current language
  const config = appConfig.getConfig();
  const currentLanguage = config.localization.languagetype;
  
  // Get domains based on app tier - update when premium status changes
  const isPremium = appConfig.isPremium();
  const domains = useMemo(() => 
    isPremium ? getPremiumDomains() : getStandardDomains(),
    [isPremium]
  );
  
  // State management
  const [selectedDomainIndex, setSelectedDomainIndex] = useState(0);
  const [milestones, setMilestones] = useState<MilestoneWithText[]>([]);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [floorProgress, setFloorProgress] = useState(false); // Progress indicator for devFloor calculation
  const [floorCeilingInfo, setFloorCeilingInfo] = useState<{floor: number, ceiling: number} | null>(null);
  const [devshowall, setDevshowall] = useState(false);
  const [debugInfoEnabled, setDebugInfoEnabled] = useState(false);
  const [devAgeResults, setDevAgeResults] = useState<DevelopmentalAgeResult | null>(null);
  const [stalenessThreshold, setStalenessThreshold] = useState('2weeks');
  const [isCalculating, setIsCalculating] = useState(false);
  const [showLastUpdate, setShowLastUpdate] = useState(true); // Default enabled to match DebugView
  const scrollViewRef = useRef<ScrollView>(null);
  const navigationDomainProcessed = useRef<string | null>(null);
  
  const selectedDomain = domains[selectedDomainIndex];
  const milestoneStorage = getSharedStorage();

  // Get stores and active profile (moved before staleness check)
  const profileStore = useProfileStore();
  const contentStore = useContentStore();
  const userStore = useUserStore();
  const { getActiveProfile, updateProfile, markProfileDirty } = profileStore();
  const activeProfile = getActiveProfile();
  const contentStoreReady = contentStore ? (contentStore.getState().records?.length > 0) : false;

  // Check if current domain needs updating (similar to HomeView logic)
  const checkCurrentDomainStaleness = (): boolean => {
    
    if (!activeProfile || !selectedDomain) {
      return false;
    }
    
    // If a calculation is currently running, don't show staleness warning
    if (isCalculating) {
      return false;
    }
    
    // Check if no milestones are answered for this domain
    const domainMilestones = milestones.filter(m => {
      const milestoneState = activeProfile.milestonestate.find((ms: MilestoneState) => ms.mvarid === m.mvar);
      const hasAnswer = milestoneState && milestoneState.state !== null;
      return hasAnswer;
    });
    
    if (domainMilestones.length === 0) {
      return true; // No milestones answered
    }
    
    // Check staleness based on computation timestamp
    const domainKey = selectedDomain.key;
    const mappedDomainKey = mapDomainKey(domainKey, appConfig.isPremium());
    const computationTimestamp = activeProfile.devresultcomputed?.[mappedDomainKey] || null;
    
    if (!computationTimestamp) {
      
      // Additional check: if we have answered milestones but no timestamp, 
      // it might be a timing issue - wait briefly and recheck
      if (domainMilestones.length > 0) {
         
        // Schedule a delayed recheck to handle race conditions
        setTimeout(() => {
          console.log('🔍 Delayed staleness recheck after milestone answers');
        }, 1000);
      }
      
      return true; // No computation timestamp
    }
    
    const staleness = checkDomainStaleness(computationTimestamp, stalenessThreshold);
    const needsUpdate = staleness.isStale || staleness.daysSinceComputed === null;
    
    return needsUpdate;
  };

  const currentDomainNeedsUpdate = useMemo(() => {
    return checkCurrentDomainStaleness();
  }, [milestones, activeProfile, selectedDomain, stalenessThreshold]);

  // Handle domain selection from navigation props
  useEffect(() => {
    if (propSelectedDomain && domains.length > 0 && propSelectedDomain !== navigationDomainProcessed.current) {
      const domainIndex = domains.findIndex(domain => domain.key === propSelectedDomain);
      if (domainIndex !== -1) {
        console.log('🎯 Setting domain from navigation:', propSelectedDomain, 'index:', domainIndex);
        setSelectedDomainIndex(domainIndex);
        navigationDomainProcessed.current = propSelectedDomain;
        // Clear the navigation domain immediately after setting it so arrow navigation works
        if (onDomainSelected) {
          onDomainSelected(); 
        }
      }
    }
  }, [propSelectedDomain, domains, onDomainSelected]);

  // Clear navigation domain when component unmounts
  useEffect(() => {
    return () => {
      navigationDomainProcessed.current = null;
      if (onDomainSelected) {
        onDomainSelected(); // Clear the selected domain when leaving the view
      }
    };
  }, [onDomainSelected]);

  // Load debug info state from storage (same key as AdminView)
  useEffect(() => {
    const loadDebugState = async () => {
      try {
        const globalStorage = getRuntimeStorage();
        if (!globalStorage) return;
        
        const debugState = await globalStorage.getItem('debug_show_info');
        if (debugState === 'true') {
          setDebugInfoEnabled(true);
          console.log('🐛 MilestonesView: Debug info enabled from storage');
        }
        
        const showLastUpdateState = await globalStorage.getItem('debug_show_last_update');
        if (showLastUpdateState === 'false') {
          setShowLastUpdate(false);
          console.log('🐛 MilestonesView: Show last update disabled from storage');
        }
        // Note: If no setting exists or setting is 'true', stays enabled (default)
      } catch (error) {
        console.error('MilestonesView: Failed to load debug state:', error);
      }
    };
    loadDebugState();
  }, [contentStoreReady]); // Re-run when ContentStore becomes ready

  // Helper function to get current domain's developmental age from profile
  const getCurrentDomainDevAge = (): number | null => {
    if (!activeProfile || !selectedDomain) {
      return null;
    }
    
    const domainKey = mapDomainKey(selectedDomain.key, appConfig.isPremium());
    return activeProfile.devage[domainKey];
  };

  // Helper function to get current domain's computation timestamp
  const getCurrentDomainTimestamp = (): string | null => {
    if (!activeProfile || !selectedDomain) {
      return null;
    }
    
    const mappedDomainKey = mapDomainKey(selectedDomain.key, appConfig.isPremium());
    return activeProfile.devresultcomputed?.[mappedDomainKey] || null;
  };

  // Helper function to store developmental age results in profile
  const storeResultsInProfile = (results: DevelopmentalAgeResult) => {
    if (!activeProfile || !selectedDomain) {
      console.warn('⚠️ Cannot store results - no active profile or selected domain');
      return;
    }

    const domainKey = mapDomainKey(selectedDomain.key, appConfig.isPremium());
    
    console.log(`💾 Storing developmental age results for domain ${domainKey}:`, {
      devAge: results.developmentalAge,
      delay: results.delay,
      result: results.result,
      resultScore: results.resultScore
    });

    // Update the profile with calculated results
    const computationTimestamp = new Date().toISOString();
    
    const profileUpdates = {
      devage: {
        ...activeProfile.devage,
        [domainKey]: results.developmentalAge
      },
      devdelay: {
        ...activeProfile.devdelay,
        [domainKey]: results.delay
      },
      devresult: {
        ...activeProfile.devresult,
        [domainKey]: results.result === 'typical' ? 1 : results.result === 'concern' ? 2 : 3
      },
      devresultscore: {
        ...activeProfile.devresultscore,
        [domainKey]: results.resultScore
      },
      devresultcomputed: {
        ...activeProfile.devresultcomputed,
        [domainKey]: computationTimestamp
      }
    };
    
    updateProfile(activeProfile.id, profileUpdates);
    
    // Verify the update was applied by checking the profile store
    
    // Note: The profile update is async in the store, so we can't immediately verify 
    // the change here, but this helps track when the update is initiated
  };

  // Automatically recalculate physical age from DOB when profile changes or view is entered
  useEffect(() => {
    const recalculatePhysicalAge = () => {
      if (!activeProfile || !activeProfile.dob) {
        return;
      }
      
      // Calculate current age in tenths of a month from DOB
      const currentAge = calculateAgeInMonths(activeProfile.dob);
      
      // Only update if the age has changed (avoid unnecessary updates)
      if (Math.abs(currentAge - activeProfile.agephysical) > 0.05) { // 0.05 month threshold (~1.5 days)
        
        updateProfile(activeProfile.id, {
          agephysical: currentAge,
          lastActiveDate: new Date().toISOString()
        });
      }
    };
    
    recalculatePhysicalAge();
  }, [activeProfile?.id, activeProfile?.dob, updateProfile]); // Re-run when profile ID or DOB changes
  
  // Milestone answer handling
  const getMilestoneAnswer = (mvarid: string): 'yes' | 'sometimes' | 'not-yet' | null => {
    if (!activeProfile) return null;
    const milestoneState = activeProfile.milestonestate.find((ms: MilestoneState) => ms.mvarid === mvarid);
    return milestoneState?.state || null;
  };
  
  const updateMilestoneAnswer = async (mvarid: string, answer: 'yes' | 'sometimes' | 'not-yet') => {
    if (!activeProfile || !selectedDomain) return;
    
    const updatedMilestoneStates = [...activeProfile.milestonestate];
    const existingIndex = updatedMilestoneStates.findIndex(ms => ms.mvarid === mvarid);
    
    const newMilestoneState: MilestoneState = {
      mvarid,
      state: answer,
      lastUpdated: new Date().toISOString(),
    };
    
    if (existingIndex >= 0) {
      updatedMilestoneStates[existingIndex] = newMilestoneState;
    } else {
      updatedMilestoneStates.push(newMilestoneState);
    }
    
    const updatedProfile = {
      ...activeProfile,
      milestonestate: updatedMilestoneStates,
      lastActiveDate: new Date().toISOString()
    };
    
    // Set a temporary "computing" timestamp to prevent staleness warnings during calculation
    const domainKey = mapDomainKey(selectedDomain.key, appConfig.isPremium());
    const computingTimestamp = new Date().toISOString();
    
    console.log(`🔧 updateMilestoneAnswer: Domain mapping`, {
      originalKey: selectedDomain.key,
      mappedKey: domainKey,
      isPremium: appConfig.isPremium(),
      computingTimestamp
    });
    
    updateProfile(activeProfile.id, {
      milestonestate: updatedMilestoneStates,
      lastActiveDate: new Date().toISOString(),
      devresultcomputed: {
        ...activeProfile.devresultcomputed,
        [domainKey]: computingTimestamp // Temporary timestamp during computation
      }
    });
    
    // Mark profile dirty — user has entered milestone data that needs syncing to backend
    markProfileDirty(activeProfile.id, `Milestone answer updated: ${mvarid}=${answer}`);
    
    // Recalculate floor/ceiling first — scoring must use the new devFloor/devCeiling window,
    // not the stale indices from before this answer was recorded.
    const profileWithWindow = await loadMilestonesWithProfile(updatedProfile, true);
    calculateDevelopmentalAgeOptimizedWithProfile(profileWithWindow ?? updatedProfile, 'milestone-answer').catch(error => {
      console.error('❌ Error in developmental age calculation after milestone answer:', error);
    });
  };
  
  // Helper function to run developmental age calculation with a specific profile
  const calculateDevelopmentalAgeOptimizedWithProfile = async (profile: any, triggerSource = 'manual'): Promise<void> => {
    if (!profile || !selectedDomain) {
      console.log('⏭️ Skipping dev age calculation - no profile or selected domain');
      return;
    }

    try {
      setIsCalculating(true);
      
      const domainKey = mapDomainKey(selectedDomain.key, appConfig.isPremium());
      const isPremium = appConfig.isPremium();
      
      // Prepare milestone answers from the provided profile's milestone states
      const milestoneAnswers = new Map<string, string>();
      
      for (const milestoneState of profile.milestonestate) {
        if (milestoneState.state) {
          let answerText: string;
          switch (milestoneState.state) {
            case 'yes': answerText = 'Yes'; break;
            case 'sometimes': answerText = 'Sometimes'; break;
            case 'not-yet': answerText = 'Not Yet'; break;
            default: continue;
          }
          milestoneAnswers.set(milestoneState.mvarid, answerText);
        }
      }
       
      if (milestoneAnswers.size === 0) {
        console.log('⚠️ No milestone answers found - developmental age cannot be calculated');
        setDevAgeResults(null);
        return;
      }
      
      // Use the same cached AdminNodeDto approach as the original function
      const globalStorage = getRuntimeStorage();
      if (!globalStorage) {
        console.error('❌ Global storage not available');
        setDevAgeResults(null);
        return;
      }
      
      const adminSegmentStorage = new AdminSegmentStorage(globalStorage);
      
      // Try to use pure AdminNodeDto approach first (same as original)
      const adminNodeName = mapDomainToAdminNodeName(selectedDomain.key, isPremium);
      const adminNodeDto = await adminSegmentStorage.getAdminNodeByDomain(adminNodeName);
      
      if (adminNodeDto && adminNodeDto.Probs && adminNodeDto.Means && adminNodeDto.Cuts && adminNodeDto.Probs.length > 0 && adminNodeDto.Means.length > 0 && adminNodeDto.Cuts.length > 0) {
        console.log(`✅ Found complete AdminNodeDto for ${adminNodeName} - using pure AdminNodeDto calculation`);
        
        // Convert string answers to numeric values for calculation
        const numericAnswers = new Map<string, number>();
        milestoneAnswers.forEach((answerText, mvar) => {
          let dataValue: number;
          switch (answerText) {
            case 'Yes': dataValue = 1; break;
            case 'Sometimes': dataValue = 2; break;
            case 'Not Yet': dataValue = 3; break;
            default: 
              console.warn(`⚠️ Unknown answer text: ${answerText} for mvar: ${mvar}`);
              return;
          }
          numericAnswers.set(mvar, dataValue);
        });
        
        // Create developmental age input using AdminNodeDto directly
        const input: DevelopmentalAgeInput = {
          domainNode: adminNodeDto,
          domainKey: selectedDomain.key,
          milestoneAnswers: numericAnswers,
          childPhysicalAge: profile.agephysical,
          devFloorOrderIndex: profile.devfloor[domainKey],
          devCeilingOrderIndex: profile.devceiling[domainKey],
          answerSet: {
            languageTypeId: '1',
            answers: [
              { dataValue: 1, textLabel: 'Yes', keyValue: 1 },
              { dataValue: 2, textLabel: 'Sometimes', keyValue: 2 },
              { dataValue: 3, textLabel: 'Not Yet', keyValue: 3 }
            ]
          },
          debugMode: debugInfoEnabled
        };
        
        // Use the statically imported core computation function
        const results = await computeDevelopmentalAge(input);
        setDevAgeResults(results);
        
        // Store results in profile
        storeResultsInProfile(results);
        
        return;
      }
      
      // Fallback: Use the AdminSegment-based optimized function (same as original)
      const apiService = userStore.getState().getApiService();
      if (!apiService) {
        console.error('❌ API service not available');
        setDevAgeResults(null);
        return;
      }
      
      const contentService = createcontentService(apiService, contentStore.getState());
      const milestoneType = isPremium ? 'premium' : 'standard';
      
      const results = await computeDevelopmentalAgeOptimized(
        selectedDomain.key,
        profile.agephysical,
        milestoneAnswers,
        adminSegmentStorage,
        contentService,
        '1', // languageId
        milestoneType,
        debugInfoEnabled
      );
      
      setDevAgeResults(results);
      
      // Store results in profile
      storeResultsInProfile(results);
      
    } catch (error) {
      console.log('❌ Error calculating developmental age with profile:', error);
      setDevAgeResults(null);
    } finally {
      setIsCalculating(false);
    }
  };
  
  // Helper function to load milestones with a specific profile.
  // Returns the profile with updated devfloor/devceiling when recalculateFloorCeiling=true and the window changed.
  const loadMilestonesWithProfile = async (profile: any, recalculateFloorCeiling = false): Promise<any> => {
    let profileResult: any = profile;
    try {
      if (!profile || !selectedDomain) {
        setMilestones([]);
        setFloorCeilingInfo(null);
        return profile;
      }

      // Get the domain order list for current app tier
      const domainOrderList = await getDomainOrderList(selectedDomain.key);
      if (!domainOrderList || domainOrderList.length === 0) {
        console.warn(`⚠️ No domain order list found for ${selectedDomain.key} in ${appConfig.isPremium() ? 'Premium' : 'Standard'} mode`);
        setMilestones([]);
        setFloorCeilingInfo(null);
        return;
      }
      
      // Load milestones by mvar from domain order list (same as before)
      const orderedMilestones: MilestoneWithText[] = [];
      
      for (const orderItem of domainOrderList) {
        const milestone = await milestoneStorage.getMilestoneByMvar(orderItem.mvarName, currentLanguage);
        
        if (milestone) {
          const orderedMilestone: MilestoneWithText = {
            ...milestone,
            itemage: orderItem.itemAge
          };
          orderedMilestones.push(orderedMilestone);
        } else {
          console.warn(`  ⚠️ Milestone ${orderItem.mvarName} not found in storage`);
        }
      }
      
      orderedMilestones.sort((a, b) => (a.itemage || 0) - (b.itemage || 0));
      
      const domainKey = mapDomainKey(selectedDomain.key, appConfig.isPremium());
      
      if (devshowall) {
        setMilestones(orderedMilestones);
        setFloorCeilingInfo(null);
      } else {
        if (recalculateFloorCeiling) {
          // Use getMilestoneRange with the provided profile
          const globalStorage = getRuntimeStorage();
          const result = await getMilestoneRange(profile, domainKey, orderedMilestones, globalStorage);
          
          // Update the actual profile in the store if floor/ceiling changed
          if (result.profileUpdated) {
            const updatedProfile = updateProfileDevFloorCeiling(
              profile, 
              domainKey, 
              result.floor, 
              result.ceiling,
              result.devstate
            );
            profileResult = updatedProfile; // capture for return
            updateProfile(profile.id, {
              devfloor: updatedProfile.devfloor,
              devceiling: updatedProfile.devceiling,
              devstate: updatedProfile.devstate,
              lastActiveDate: updatedProfile.lastActiveDate
            });
          }
          
          // Handle progress indicator for devFloor calculation
          if (result.showProgress) {
            setFloorProgress(true);
            // Show progress for half a second
            setTimeout(() => {
              setFloorProgress(false);
            }, 500);
          }
          
          const sortedResultMilestones = [...result.milestones].sort((a, b) => (a.itemage || 0) - (b.itemage || 0));
          
          setFloorCeilingInfo({ floor: result.floor, ceiling: result.ceiling });
          setMilestones(sortedResultMilestones);
        } else {
          // Use existing floor/ceiling from provided profile
          const currentFloor = profile.devfloor[domainKey] || 0;
          const currentCeiling = profile.devceiling[domainKey] || orderedMilestones.length;
          
          const milestonesInRange = orderedMilestones.slice(currentFloor, currentCeiling + 1);
          
          setFloorCeilingInfo({ floor: currentFloor, ceiling: currentCeiling });
          setMilestones(milestonesInRange);
        }
      }
      return profileResult;
    } catch (error) {
      console.error('Failed to load milestones with profile:', error);
      setMilestones([]);
      setFloorCeilingInfo(null);
    } finally {
      setLoading(false);
    }
  };

  // Optimized developmental age calculation function
  const calculateDevelopmentalAgeOptimized = async (triggerSource = 'manual'): Promise<void> => {
    if (!activeProfile || !selectedDomain) {
      console.log('⏭️ Skipping dev age calculation - no active profile or selected domain');
      return;
    }

    try {
      const domainKey = mapDomainKey(selectedDomain.key, appConfig.isPremium());
      const isPremium = appConfig.isPremium();
      const milestoneType = isPremium ? 'premium' : 'standard';
      
      // Prepare milestone answers from user's saved milestone states
      const milestoneAnswers = new Map<string, string>();
      
      // Only use actual user-entered milestone states
      for (const milestoneState of activeProfile.milestonestate) {
        if (milestoneState.state) {
          let answerText: string;
          switch (milestoneState.state) {
            case 'yes': answerText = 'Yes'; break;
            case 'sometimes': answerText = 'Sometimes'; break;
            case 'not-yet': answerText = 'Not Yet'; break;
            default: continue;
          }
          milestoneAnswers.set(milestoneState.mvarid, answerText);
        }
      }
      
      if (milestoneAnswers.size === 0) {
        console.log('⚠️ No milestone answers found - developmental age cannot be calculated');
        setDevAgeResults(null);
        return;
      }
      
      // Get global storage and content sync service
      const globalStorage = getRuntimeStorage();
      if (!globalStorage) {
        console.log('❌ Global storage not available');
        setDevAgeResults(null);
        return;
      }
      
      const adminSegmentStorage = new AdminSegmentStorage(globalStorage);
      
      // Get API service from userStore instead of passing ConfigManager
      const apiService = userStore.getState().getApiService();
      if (!apiService) {
        console.error('❌ API service not available');
        setDevAgeResults(null);
        return;
      }
      
      const contentService = createcontentService(apiService, contentStore.getState());
      
      // Try to use pure AdminNodeDto approach first (no AdminSegment needed)
      const adminNodeName = mapDomainToAdminNodeName(selectedDomain.key, isPremium);
      const adminNodeDto = await adminSegmentStorage.getAdminNodeByDomain(adminNodeName);
      
      if (adminNodeDto && adminNodeDto.Probs && adminNodeDto.Means && adminNodeDto.Cuts && adminNodeDto.Probs.length > 0 && adminNodeDto.Means.length > 0 && adminNodeDto.Cuts.length > 0) {
        
        // Convert string answers to numeric values for calculation
        const numericAnswers = new Map<string, number>();
        milestoneAnswers.forEach((answerText, mvar) => {
          let dataValue: number;
          switch (answerText) {
            case 'Yes': dataValue = 1; break;
            case 'Sometimes': dataValue = 2; break;
            case 'Not Yet': dataValue = 3; break;
            default: 
              console.warn(`⚠️ Unknown answer text: ${answerText} for mvar: ${mvar}`);
              return; // Skip unknown answers
          }
          numericAnswers.set(mvar, dataValue);
        });
              
        // Create developmental age input using AdminNodeDto directly
        const input: DevelopmentalAgeInput = {
          domainNode: adminNodeDto,
          domainKey: selectedDomain.key,
          milestoneAnswers: numericAnswers,
          childPhysicalAge: activeProfile.agephysical,
          devFloorOrderIndex: activeProfile.devfloor[domainKey],
          devCeilingOrderIndex: activeProfile.devceiling[domainKey],
          answerSet: {
            languageTypeId: '1',
            answers: [
              { dataValue: 1, textLabel: 'Yes', keyValue: 1 },
              { dataValue: 2, textLabel: 'Sometimes', keyValue: 2 },
              { dataValue: 3, textLabel: 'Not Yet', keyValue: 3 }
            ]
          },
          debugMode: debugInfoEnabled
        };
        
        // Use the statically imported core computation function
        const results = await computeDevelopmentalAge(input);
        setDevAgeResults(results);
        
        // Store results in profile
        storeResultsInProfile(results);
        
        return;
      }
      
      // Fallback: Use the AdminSegment-based optimized function
      console.log('🔄 Falling back to AdminSegment-based calculation...');
      const results = await computeDevelopmentalAgeOptimized(
        selectedDomain.key,
        activeProfile.agephysical,
        milestoneAnswers,
        adminSegmentStorage,
        contentService,
        '1', // languageId
        milestoneType,
        debugInfoEnabled
      );
      
      setDevAgeResults(results);
      
      // Store results in profile
      storeResultsInProfile(results);
      
      console.log('✅ Developmental age calculation complete:', {
        developmentalAge: results.developmentalAge,
        delay: results.delay,
        result: results.result,
        resultScore: results.resultScore,
        domainResults: results.domainResults ? {
          score: results.domainResults.scoreValue,
          delay: results.domainResults.delayValue,
          result: results.domainResults.resultValue,
          resultScore: results.domainResults.resultScoreValue
        } : null
      });
    } catch (error) {
      console.error('❌ Error calculating developmental age:', error);
      setDevAgeResults(null);
    } finally {
      // Clear loading state (only for manual trigger like refresh)
      if (triggerSource === 'manual') {
        setLoading(false);
      }
    }
  };

  // Reusable developmental age calculation function
  const calculateDevelopmentalAge = async (triggerSource = 'manual'): Promise<void> => {
    if (!activeProfile || !selectedDomain) {
      console.log('⏭️ Skipping dev age calculation - no active profile or selected domain');
      return;
    }

    try {
      const domainKey = mapDomainKey(selectedDomain.key, appConfig.isPremium());
      const isPremium = appConfig.isPremium();
      const adminNodeName = mapDomainToAdminNodeName(selectedDomain.key, isPremium);
     
      // Get the appropriate AdminSegment ID based on app tier
      const segmentId = isPremium ? '11270' : '11255';  // Premium vs Standard
      const languageId = '1'; // Default language
      
      // Check if AdminSegment is already available in content store
      const contentStoreState = contentStore.getState();
      const query = { contentType: 'AdminSegment', contentTypeId: segmentId, languageTypeId: languageId };
      
      // Debug: Log detailed content store state
      logContentStoreDebugInfo(contentStore, `MILESTONE_${triggerSource.toUpperCase()}`);
      
      // Check if the specific AdminSegment exists
      const adminSegmentExists = hasAdminSegment(contentStore, segmentId, languageId);
      
      if (adminSegmentExists) {
        const segmentInfo = getAdminSegmentInfo(contentStore, segmentId, languageId);
      }
      
      let contentRecord = contentStoreState.getContent(query);
      
      if (!contentRecord || !contentRecord.tableData) {
        console.warn('⚠️ AdminSegment', segmentId, 'not found in content store, attempting to load...');
        
        // List available AdminSegments in store
        const records = contentStoreState.records || [];
        const adminSegments = records.filter((r: any) => r.contentType === 'AdminSegment');
        const availableSegments = adminSegments.map((record: any) => 
          `${record.contentTypeId} (Lang: ${record.languageTypeId})`
        ).join(', ') || 'None';
        
        try {
          // Implement proper automatic backend loading
          const globalStorage = getRuntimeStorage();
          if (!globalStorage) {
            throw new Error('Global storage not available');
          }
          
          // Set loading state while auto-loading (only for manual trigger like refresh)
          if (triggerSource === 'manual') {
            setLoading(true);
          }
          
          // Check authentication status before attempting backend call
          const userState = userStore.getState();
          const isAuthenticated = userState.session.isAuthenticated;
          const isSessionValid = userState.isSessionValid();
          
          if (!isAuthenticated || !isSessionValid) {
            
            // Attempt to refresh authentication from stored credentials
            try {
              const refreshed = await userState.restoreSessionFromStorage();
              if (refreshed) {
                console.log('✅ Authentication refreshed successfully');
              } else {
                // If refresh fails, try re-login with stored credentials
                console.log('🔑 Refresh failed - attempting re-login...');
                
                const storedEmail = await globalStorage.getItem('user_email');
                if (storedEmail) {
                  // For development, we can use the dev credentials
                  const isDev = typeof __DEV__ !== 'undefined' ? __DEV__ : false;
                  if (isDev) {
                    console.log('🔧 Development mode - attempting auto re-login...');
                    const loginSuccess = await userState.login(
                      '',
                      ''
                    );
                    if (loginSuccess) {
                      console.log('✅ Development auto re-login successful');
                    } else {
                      throw new Error('Development auto re-login failed');
                    }
                  } else {
                    // In production, we cannot auto-login without stored password
                    throw new Error('Authentication expired - user must manually log in');
                  }
                } else {
                  throw new Error('No stored credentials available for re-authentication');
                }
              }
            } catch (authError) {
              console.error('❌ Re-authentication failed:', authError);
              throw new Error(`Authentication failed: ${authError instanceof Error ? authError.message : String(authError)}`);
            }
          }
          
          // Now try to auto-load the AdminSegment using contentService
          // First check if we have a contentService with properly initialized API service
          const contentStoreState = contentStore.getState();
          
          // Try to get API service from userStore (should now be authenticated)
          const apiService = userStore.getState().getApiService();
          if (!apiService) {
            throw new Error('API service not available in user store - cannot load AdminSegment from backend');
          }
          
          console.log('✅ API service found, creating contentService...');
          
          // Create contentService with the authenticated API service
          const contentService = createcontentService(apiService, contentStoreState);
          
          // Use the same approach as AdminView - make direct service request
          if (contentService) {
            console.log('🔄 Using contentService.getContent method...');
            
            try {
              const tableData = await contentService.getContent(
                'AdminSegment',
                segmentId,
                languageId,
                { forceRefresh: true }
              );
              
              if (tableData) {
                console.log('✅ Successfully received AdminSegment', segmentId, 'from backend');
                
                // Add to content store for future use
                contentStore.getState().addOrUpdateContent({
                  contentType: 'AdminSegment',
                  contentTypeId: segmentId,
                  languageTypeId: languageId
                }, tableData);
                
                // CRITICAL: Parse and store AdminNodeDto objects locally
                console.log('🔧 Parsing AdminSegment to extract and store AdminNodeDto objects...');
                try {
                  const adminSegmentStorage = new AdminSegmentStorage(globalStorage);
                  
                  // Create a ContentRecord from the tableData for unpacking
                  const tempContentRecord = {
                    id: `AdminSegment_${segmentId}_${languageId}`,
                    contentType: 'AdminSegment',
                    contentTypeId: segmentId,
                    languageTypeId: languageId,
                    tableData: tableData,
                    quickAccess: toMemoryOptimized(tableData),
                    lastUpdated: new Date().toISOString(),
                    lastSynced: new Date().toISOString(),
                    syncVersion: '1.0',
                    isOutdated: false
                  };
                  
                  // Unpack AdminSegment to extract and store AdminNodeDto objects
                  const unpackedAdminSegment = await adminSegmentStorage.unpackAdminSegment(tempContentRecord);
                  
                  if (unpackedAdminSegment) {
                    console.log('✅ AdminSegment unpacked and AdminNodeDto objects stored locally');
                    console.log(`   - AdminSegment: ${unpackedAdminSegment.Title}`);
                    console.log(`   - Nodes: ${unpackedAdminSegment.Nodes?.length || 0} domains available`);
                    if (unpackedAdminSegment.Nodes) {
                      console.log(`   - Domain names: ${unpackedAdminSegment.Nodes.map(n => n.Name).join(', ')}`);
                    }
                  } else {
                    console.warn('⚠️ Failed to unpack AdminSegment - AdminNodeDto objects may not be available');
                  }
                } catch (parseError) {
                  console.error('❌ Failed to parse AdminSegment for AdminNodeDto storage:', parseError);
                  // Don't fail the whole process, just log the error
                }
                
                // Re-check content store after auto-load - it should now be available
                console.log('🔍 Re-checking content store after adding AdminSegment...');
                const updatedContentStoreState = contentStore.getState();
                
                // Debug: Log the query and available records
                console.log('🔍 Using query to find AdminSegment:', query);
                const allAdminSegments = updatedContentStoreState.records?.filter((r: any) => r.contentType === 'AdminSegment') || [];
                console.log('🔍 All AdminSegments in store after auto-load:',
                  allAdminSegments.map((r: any) => `ID:${r.contentTypeId} Lang:${r.languageTypeId} HasData:${!!r.tableData}`)
                );
                
                contentRecord = updatedContentStoreState.getContent(query);
                
                if (!contentRecord || !contentRecord.tableData) {
                  console.error('❌ AdminSegment still not available after successful backend fetch');
                  console.log('🔍 Debug: Query used:', query);
                  console.log('🔍 Debug: getContent method available:', typeof updatedContentStoreState.getContent);
                  
                  // Try alternative lookup methods
                  console.log('🔄 Attempting alternative record lookup...');
                  const alternativeRecord = allAdminSegments.find((r: any) => 
                    r.contentTypeId === segmentId // AdminSegments are stored once for all languages
                  );
                  
                  if (alternativeRecord) {
                    console.log('✅ Found AdminSegment using alternative lookup method');
                    contentRecord = alternativeRecord;
                  } else {
                    throw new Error('AdminSegment not properly stored after backend fetch');
                  }
                }
                
                console.log('✅ AdminSegment now available in content store after auto-load');
              } else {
                throw new Error('Backend returned no data for AdminSegment ' + segmentId);
              }
            } catch (serviceError) {
              console.error('❌ contentService.getContent failed:', serviceError);
              throw new Error(`Failed to fetch AdminSegment: ${serviceError instanceof Error ? serviceError.message : String(serviceError)}`);
            }
          } else {
            throw new Error('contentService not available');
          }
        } catch (loadError) {
          console.error('❌ Automatic AdminSegment loading failed:', loadError);
          console.log('');
          console.log('💡 AdminSegment', segmentId, 'could not be loaded automatically:');
          console.log('   🔍 Error details:', loadError instanceof Error ? loadError.message : String(loadError));
          
          // If we were trying to load premium segment (11270) and it failed, try standard fallback
          if (segmentId === '11270' && isPremium) {
            console.log('');
            console.log('🔄 Attempting fallback to Standard AdminSegment (11255) for premium users...');
            try {
              const fallbackSegmentId = '11255';
              const fallbackQuery = { contentType: 'AdminSegment', contentTypeId: fallbackSegmentId, languageTypeId: languageId };
              
              let fallbackContentRecord = contentStoreState.getContent(fallbackQuery);
              if (fallbackContentRecord && fallbackContentRecord.tableData) {
                console.log('✅ Standard AdminSegment 11255 found in content store, using as fallback');
                // Update the contentRecord to use the fallback
                contentRecord = fallbackContentRecord;
                console.log('✅ Successfully switched to Standard AdminSegment as fallback');
              } else {
                console.log('⚠️ Standard AdminSegment 11255 also not available in content store');
                setDevAgeResults(null);
                return;
              }
            } catch (fallbackError) {
              console.error('❌ Fallback to standard AdminSegment also failed:', fallbackError);
              setDevAgeResults(null);
              return;
            }
          } else {
            console.log('');
            console.log('🔧 Possible solutions:');
            console.log('   1. Check network connectivity to backend server');
            console.log('   2. Verify authentication credentials are valid');
            console.log('   3. Ensure AdminSegment', segmentId, 'exists in backend database');
            console.log('   4. Check backend server status and API endpoints');
            console.log('   5. Try logging out and logging back in manually');
            console.log('');
            console.log('');
            console.log('📋 Alternative: Manual loading via AdminView:');
            console.log('   • Go to any assessment view (AdminView)');
            console.log('   • Use debug button: ⬇️ Get AdminSegment 11255');
            console.log('   • Return to MilestonesView and retry');
            
            setDevAgeResults(null);
            return;
          }
        }
      }
      
      console.log('✅ AdminSegment is now available - either from content store or auto-loaded from backend');
      
      // Create AdminSegmentStorage and get the AdminSegment data
      const globalStorage = getRuntimeStorage();
      if (!globalStorage) {
        console.error('❌ Global storage not available');
        setDevAgeResults(null);
        return;
      }
      
      const adminSegmentStorage = new AdminSegmentStorage(globalStorage);
      let adminSegment;
      
      // AdminSegment should now be available in content store (either was there or just loaded)
      if (contentRecord && contentRecord.tableData) {
        console.log('🔧 Unpacking AdminSegment from content store...');
        adminSegment = await adminSegmentStorage.unpackAdminSegment(contentRecord);
      } else {
        console.error('❌ ContentRecord missing or has no tableData after loading process');
        setDevAgeResults(null);
        return;
      }
      
      if (!adminSegment) {
        console.error('❌ Failed to unpack AdminSegment data');
        setDevAgeResults(null);
        return;
      }
      
      console.log('✅ AdminSegment unpacked:', adminSegment.Title);
      
      // Check if nodes are available in the expected format, otherwise use raw data
      let availableNodes = adminSegment.Nodes || [];
      if (availableNodes.length === 0 && adminSegment.Nodes) {
        console.log('🔄 Using raw Nodes data from AdminSegmentDto');
        availableNodes = adminSegment.Nodes;
      }
      
      console.log('🔍 Available domain nodes:', availableNodes.map((n: any) => n.Name) || []);
      
      // Find the domain node that matches our selected domain using the AdminNodeDto name
      const domainNode = availableNodes.find((node: any) => {
        // Match by correctly mapped AdminNodeDto name (e.g., CDC_LC, CDCX_LC, etc.)
        return node.Name === adminNodeName;
      });
      
      if (!domainNode) {
        console.error('❌ Could not find domain node for', adminNodeName, 'in AdminSegment');
        console.log('Available nodes:', availableNodes.map((n: any) => `${n.Name} (ID: ${n.ID})`) || []);
        setDevAgeResults(null);
        return;
      }
      
      console.log('✅ Found domain node:', domainNode.Name);
      
      // Prepare milestone answers from user's saved milestone states only
      const milestoneAnswers = new Map<string, number>();
      
      // Only use actual user-entered milestone states - never generate synthetic answers
      for (const milestoneState of activeProfile.milestonestate) {
        if (milestoneState.state) {
          let dataValue: number;
          switch (milestoneState.state) {
            case 'yes': dataValue = 1; break;
            case 'sometimes': dataValue = 2; break;
            case 'not-yet': dataValue = 3; break;
            default: continue;
          }
          milestoneAnswers.set(milestoneState.mvarid, dataValue);
        }
      }
      
      console.log('📊 Total user-entered milestone answers available:', milestoneAnswers.size);
      
      if (milestoneAnswers.size === 0) {
        console.log('⚠️ No milestone answers found - developmental age cannot be calculated');
        setDevAgeResults(null);
        return;
      }
      
      // Create input for developmental age calculation
      const input: DevelopmentalAgeInput = {
        domainNode,
        domainKey, // Add domain key for proper result variable naming
        milestoneAnswers,
        childPhysicalAge: activeProfile.agephysical,
        devFloorOrderIndex: activeProfile.devfloor[domainKey],
        devCeilingOrderIndex: activeProfile.devceiling[domainKey],
        answerSet: {
          languageTypeId: languageId,
          answers: [
            { dataValue: 1, textLabel: 'Yes', keyValue: 1 },
            { dataValue: 2, textLabel: 'Sometimes', keyValue: 2 },
            { dataValue: 3, textLabel: 'Not Yet', keyValue: 3 }
          ]
        },
        debugMode: debugInfoEnabled
      };
      
      console.log('🧮 Computing developmental age with', milestoneAnswers.size, 'user-entered milestone answers');
      const results = await computeDevelopmentalAge(input);
      setDevAgeResults(results);
      
      console.log('✅ Developmental age calculation complete:', {
        developmentalAge: results.developmentalAge,
        delay: results.delay,
        result: results.result,
        resultScore: results.resultScore,
        domainResults: results.domainResults ? {
          score: results.domainResults.scoreValue,
          delay: results.domainResults.delayValue,
          result: results.domainResults.resultValue,
          resultScore: results.domainResults.resultScoreValue
        } : null
      });
    } catch (error) {
      console.error('❌ Error calculating developmental age:', error);
      setDevAgeResults(null);
    } finally {
      // Clear loading state (only for manual trigger like refresh)
      if (triggerSource === 'manual') {
        setLoading(false);
      }
    }
  };
  
  // Get button background color based on answer and selection state
  const getButtonBackgroundColor = (answer: 'yes' | 'sometimes' | 'not-yet', isSelected: boolean): string => {
    if (!isSelected) {
      return '#f8f9fa'; // Default unselected background
    }
    
    const config = appConfig.getConfig();
    switch (answer) {
      case 'yes':
        return getColor(config, 'green');
      case 'sometimes':
        return getColor(config, 'lightGreen');
      case 'not-yet':
        return getColor(config, 'blue');
      default:
        return '#f8f9fa';
    }
  };
  
  // Get milestone container background color when devshowall is true
  const getMilestoneContainerBackgroundColor = (mvar: string): string => {
    if (!devshowall) {
      return '#ffffff'; // Default white background
    }
    
    const answer = getMilestoneAnswer(mvar);
    if (!answer) {
      return '#ffffff'; // No color for blank answers
    }
    
    const config = appConfig.getConfig();
    switch (answer) {
      case 'yes':
        return getColor(config, 'green');
      case 'sometimes':
        return getColor(config, 'lightGreen');
      case 'not-yet':
        return getColor(config, 'blue');
      default:
        return '#ffffff';
    }
  };
  
  // Helper function to get the appropriate domain order list from stored AdminNodeDto data
  // Helper function to parse domain order data from AdminSegment data
  const parseDomainOrderFromAdminSegmentData = async (adminSegmentData: any, domainKey: string, isPremium: boolean, segmentId: string): Promise<Array<{ mvarName: string, itemAge: number, orderIndex: number }> | null> => {
    try {
      // Look for the specific domain in the AdminSegment
      const adminNodeName = mapDomainToAdminNodeName(domainKey, isPremium);
      
      console.log(`🔍 DEBUG: Parsing AdminSegment ${segmentId} for domain ${domainKey} → ${adminNodeName}`);
      console.log(`🔍 DEBUG: AdminSegment data type:`, typeof adminSegmentData);
      console.log(`🔍 DEBUG: AdminSegment data keys:`, adminSegmentData && typeof adminSegmentData === 'object' ? Object.keys(adminSegmentData) : 'Not an object');
      
      // AdminSegments contain multiple domains - find the one matching our target
      let domainOrderData: Array<{ mvarName: string, itemAge: number, orderIndex: number }> = [];
      
      // Parse through adminSegmentData to find domain-specific order information
      if (adminSegmentData && typeof adminSegmentData === 'object') {
        // Check if this AdminSegment has the expected structure
        // AdminSegments typically have multiple domain nodes each with their own Order arrays
        
        // First, try direct key matching
        console.log(`🔍 DEBUG: Looking for direct key match: ${adminNodeName}`);
        for (const [key, value] of Object.entries(adminSegmentData)) {
          console.log(`🔍 DEBUG: Checking key: ${key}, has value: ${!!value}, value type: ${typeof value}`);
          
          if (key === adminNodeName && value && typeof value === 'object') {
            const domainData = value as any;
            console.log(`🔍 DEBUG: Found matching key ${adminNodeName}, checking for Order array...`);
            console.log(`🔍 DEBUG: Domain data keys:`, Object.keys(domainData));
            
            if (domainData.Order && Array.isArray(domainData.Order)) {
              console.log(`✅ DEBUG: Found Order array with ${domainData.Order.length} items`);
              domainOrderData = domainData.Order.map((orderItem: any, index: number) => ({
                mvarName: orderItem.Name || orderItem.mvarName,
                itemAge: parseFloat(orderItem.Val || orderItem.itemAge),
                orderIndex: index
              })).filter((item: { mvarName: string, itemAge: number, orderIndex: number }) => item.mvarName);
              
              console.log(`✅ Extracted ${domainOrderData.length} milestone orders from AdminSegment ${segmentId} for ${adminNodeName}`);
              break;
            } else {
              console.log(`⚠️ DEBUG: Domain ${adminNodeName} found but no Order array or not an array`);
            }
          }
        }
        
        // If no direct match, try alternative approaches
        if (domainOrderData.length === 0) {
          console.log(`🔍 DEBUG: No direct key match found, trying alternative approaches...`);
          
          // Check if AdminSegment has a Nodes array structure
          if (adminSegmentData.Nodes && Array.isArray(adminSegmentData.Nodes)) {
            console.log(`🔍 DEBUG: Found Nodes array with ${adminSegmentData.Nodes.length} nodes`);
            const matchingNode = adminSegmentData.Nodes.find((node: any) => node.Name === adminNodeName);
            if (matchingNode && matchingNode.Order && Array.isArray(matchingNode.Order)) {
              console.log(`✅ DEBUG: Found matching node in Nodes array with Order data`);
              domainOrderData = matchingNode.Order.map((orderItem: any, index: number) => ({
                mvarName: orderItem.Name || orderItem.mvarName,
                itemAge: parseFloat(orderItem.Val || orderItem.itemAge),
                orderIndex: index
              })).filter((item: { mvarName: string, itemAge: number, orderIndex: number }) => item.mvarName);
            }
          }
          
          // Try fuzzy matching if exact match fails
          if (domainOrderData.length === 0) {
            console.log(`🔍 DEBUG: Trying fuzzy matching for domain keys...`);
            const domainLower = domainKey.toLowerCase();
            const adminNodeLower = adminNodeName.toLowerCase();
            
            for (const [key, value] of Object.entries(adminSegmentData)) {
              const keyLower = key.toLowerCase();
              if ((keyLower.includes(domainLower) || keyLower.includes(adminNodeLower.replace('_', '').replace('x', ''))) && value && typeof value === 'object') {
                const domainData = value as any;
                console.log(`🔍 DEBUG: Fuzzy match found: ${key}, checking for Order...`);
                if (domainData.Order && Array.isArray(domainData.Order)) {
                  console.log(`✅ DEBUG: Fuzzy match has Order array with ${domainData.Order.length} items`);
                  domainOrderData = domainData.Order.map((orderItem: any, index: number) => ({
                    mvarName: orderItem.Name || orderItem.mvarName,
                    itemAge: parseFloat(orderItem.Val || orderItem.itemAge),
                    orderIndex: index
                  })).filter((item: { mvarName: string, itemAge: number, orderIndex: number }) => item.mvarName);
                  
                  console.log(`✅ Fuzzy extracted ${domainOrderData.length} milestone orders from AdminSegment ${segmentId} for ${key} (target: ${adminNodeName})`);
                  break;
                }
              }
            }
          }
        }
      }
      
      if (domainOrderData.length === 0) {
        console.warn(`⚠️ No domain order data found in AdminSegment ${segmentId} for ${adminNodeName}`);
        console.log(`🔍 DEBUG: All available keys in AdminSegment:`, adminSegmentData && typeof adminSegmentData === 'object' ? Object.keys(adminSegmentData) : 'No keys');
        console.log(`🎯 DEBUG: Target domain key: ${domainKey}, AdminNode name: ${adminNodeName}, Premium: ${isPremium}`);
        return null;
      }
      
      console.log(`✅ Successfully parsed ${domainOrderData.length} domain orders from AdminSegment ${segmentId}`);
      return domainOrderData;
    } catch (error) {
      console.error('❌ Error parsing AdminSegment data:', error);
      console.log(`🔍 DEBUG: Error details:`, error instanceof Error ? error.message : String(error));
      return null;
    }
  };

  // Fallback function to load domain order from AdminSegment when AdminNodeDto is not available
  const loadDomainOrderFromAdminSegment = async (domainKey: string, isPremium: boolean, userStore: any, contentStore: any): Promise<Array<{ mvarName: string, itemAge: number, orderIndex: number }> | null> => {
    try {
      const globalStorage = getRuntimeStorage();
      if (!globalStorage) return null;

      const languageId = '1'; // Default language
      const segmentId = isPremium ? '11270' : '11255';
      console.log(`🔄 Loading domain order from AdminSegment ${segmentId} for domain: ${domainKey}`);

      // FIRST: Try to reload milestone data from bundled content before attempting authentication
      try {
        console.log(`🎯 BUNDLED CONTENT ATTEMPT: Trying to reload ${isPremium ? 'Premium' : 'Standard'} milestones from bundled content...`);
        
        const adminSegmentStorage = new AdminSegmentStorage(globalStorage);
        
        // Create a contentService using the current contentStore (which has bundled content)
        // Pass null for apiService to avoid authentication requirements
        let bundledcontentService: any = null;
        try {
          bundledcontentService = {
            getContent: async (contentType: string, contentTypeId: string, languageId: string) => {
              // Try to get content from the content store directly without API calls
              const query = { contentType, contentTypeId, languageTypeId: languageId };
              const content = contentStore.getState().getContent(query);
              if (content && content.tableData) {
                console.log(`✅ Found ${contentType} ${contentTypeId} in bundled content`);
                return content.tableData;
              }
              console.log(`⚠️ ${contentType} ${contentTypeId} not found in bundled content`);
              return null;
            }
          };
          
          // Attempt to reload milestone data from bundled content
          if (isPremium) {
            await adminSegmentStorage.reloadPremiumMilestones(bundledcontentService, languageId);
          } else {
            await adminSegmentStorage.reloadStandardMilestones(bundledcontentService, languageId);
          }
          
          console.log(`✅ BUNDLED CONTENT SUCCESS: ${isPremium ? 'Premium' : 'Standard'} milestone data reloaded from bundled content`);
          
        } catch (reloadError) {
          console.log(`⚠️ BUNDLED CONTENT RELOAD FAILED:`, reloadError);
          throw reloadError; // Re-throw to go to outer catch block
        }
        
        // Now try to get the domain order from the freshly loaded milestone data
        const domainPasData = await adminSegmentStorage.getDomainPasData();
        if (domainPasData && domainPasData[domainKey] && domainPasData[domainKey].order) {
          const orderData = domainPasData[domainKey].order;
          console.log(`🎯 BUNDLED CONTENT SUCCESS: Found domain order for ${domainKey} with ${orderData.length} items`);
          return orderData;
        } else {
          console.log(`⚠️ BUNDLED CONTENT PARTIAL: Milestone data loaded but no domain order found for ${domainKey}`);
        }
        
      } catch (bundledError) {
        console.log(`⚠️ BUNDLED CONTENT FAILED: Could not reload from bundled content:`, bundledError);
      }

      // SECOND: Fall back to authenticated contentService approach
      console.log(`🔐 AUTHENTICATION FALLBACK: Attempting authenticated AdminSegment loading...`);
      
      const apiService = userStore.getState().getApiService();
      if (!apiService) {
        console.error('❌ API service not available for AdminSegment fallback');
        return null;
      }

      console.log(`🔐 DEBUG: API service debug info:`);
      console.log(`  - Has processServiceRequest: ${!!apiService.processServiceRequest}`);
      console.log(`  - Has getAuthToken: ${!!apiService.getAuthToken}`);
      
      if (apiService.getAuthToken) {
        const token = apiService.getAuthToken();
        console.log(`  - Current token: ${token ? 'SET' : 'NOT SET'}`);
        if (token) {
          console.log(`  - Token length: ${token.length}`);
          console.log(`  - Token start: ${token.substring(0, 20)}...`);
          
          // Try to decode the JWT to see what's inside
          try {
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              console.log(`  - Token payload:`, {
                exp: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'No expiration',
                iat: payload.iat ? new Date(payload.iat * 1000).toISOString() : 'No issued at',
                sub: payload.sub || 'No subject',
                roles: payload.roles || payload.role || 'No roles',
                isPremium: payload.isPremium || payload.premium || 'No premium flag'
              });
              
              // Check if token is expired
              if (payload.exp) {
                const now = Math.floor(Date.now() / 1000);
                const isExpired = now > payload.exp;
                console.log(`  - Token expired: ${isExpired} (now: ${now}, exp: ${payload.exp})`);
              }
            }
          } catch (decodeError) {
            console.log(`  - Token decode failed:`, decodeError);
          }
        }
      }

      const contentService = createcontentService(apiService, contentStore.getState());

      try {
        // Use the correct contentService method to load AdminSegment
        console.log(`🔐 DEBUG: About to request AdminSegment ${segmentId} with authentication`);
        console.log(`🔐 DEBUG: API service available: ${!!apiService}`);
        console.log(`🔐 DEBUG: contentService available: ${!!contentService}`);
        
        const adminSegmentData = await contentService.getContent(
          'AdminSegment',
          segmentId,
          languageId,
          { forceRefresh: true }
        );
        
        if (!adminSegmentData) {
          console.error(`❌ AdminSegment ${segmentId} returned no data`);
          return null;
        }

        console.log(`✅ Successfully loaded AdminSegment ${segmentId} from backend`);
        console.log(`🔍 DEBUG: AdminSegment data type: ${typeof adminSegmentData}`);
        console.log(`🔍 DEBUG: AdminSegment has keys: ${adminSegmentData && typeof adminSegmentData === 'object' ? Object.keys(adminSegmentData).length : 0}`);
        
        // CRITICAL FIX: Properly unpack the AdminSegment to get the decoded AdminSegmentDto
        console.log(`🔧 Unpacking AdminSegment ${segmentId} to get decoded AdminSegmentDto with Nodes array...`);
        try {
          const adminSegmentStorage = new AdminSegmentStorage(globalStorage);
          
          // Create a ContentRecord from the TableData for unpacking
          const tempContentRecord = {
            id: `AdminSegment_${segmentId}_${languageId}`,
            contentType: 'AdminSegment',
            contentTypeId: segmentId,
            languageTypeId: languageId,
            tableData: adminSegmentData,
            quickAccess: toMemoryOptimized(adminSegmentData),
            lastUpdated: new Date().toISOString(),
            lastSynced: new Date().toISOString(),
            syncVersion: '1.0',
            isOutdated: false
          };
          
          // Unpack to get the proper AdminSegmentDto with Nodes array
          const unpackedAdminSegment = await adminSegmentStorage.unpackAdminSegment(tempContentRecord);
          
          if (!unpackedAdminSegment) {
            console.error(`❌ Failed to unpack AdminSegment ${segmentId}`);
            return null;
          }
          
          console.log(`✅ AdminSegment ${segmentId} unpacked successfully`);
          console.log(`   - Title: ${unpackedAdminSegment.Title}`);
          console.log(`   - Nodes: ${unpackedAdminSegment.Nodes?.length || 0} domains available`);
          if (unpackedAdminSegment.Nodes) {
            console.log(`   - Domain names: ${unpackedAdminSegment.Nodes.map(n => n.Name).join(', ')}`);
          }
          
          // Now parse the properly unpacked AdminSegmentDto
          return await parseDomainOrderFromAdminSegmentData(unpackedAdminSegment, domainKey, isPremium, segmentId);
          
        } catch (unpackError) {
          console.error(`❌ Failed to unpack AdminSegment ${segmentId}:`, unpackError);
          return null;
        }
        
      } catch (adminSegmentError: any) {
        console.error('❌ Error loading AdminSegment for domain order fallback:', adminSegmentError);
        
        // Provide detailed error analysis
        if (adminSegmentError.response) {
          console.log(`🔍 ERROR ANALYSIS:`);
          console.log(`  - HTTP Status: ${adminSegmentError.response.status}`);
          console.log(`  - Response Headers:`, adminSegmentError.response.headers);
          console.log(`  - Response Data:`, adminSegmentError.response.data);
          
          if (adminSegmentError.response.status === 401) {
            console.log(`🔐 AUTHENTICATION ERROR DETAILS:`);
            console.log(`  - Requested AdminSegment: ${segmentId} (${isPremium ? 'Premium' : 'Standard'})`);
            console.log(`  - User session status:`, {
              isAuthenticated: userStore.getState().session?.isAuthenticated,
              isSessionValid: userStore.getState().isSessionValid?.(),
              userEmail: userStore.getState().session?.user?.email
            });
            
            // Check if this might be a premium access issue
            if (segmentId === '11270') {
              console.log(`💳 PREMIUM ACCESS ANALYSIS:`);
              console.log(`  - This is premium content (AdminSegment 11270)`);
              console.log(`  - User may not have premium subscription or token lacks premium permissions`);
              console.log(`  - Backend is rejecting the request despite valid authentication`);
            }
          }
        } else if (adminSegmentError.code) {
          console.log(`🌐 NETWORK ERROR:`, adminSegmentError.code, adminSegmentError.message);
        } else {
          console.log(`❓ UNKNOWN ERROR:`, adminSegmentError.message || adminSegmentError);
        }
        
        return null;
      }
      
    } catch (error) {
      console.error('❌ Error in AdminSegment domain order fallback:', error);
      return null;
    }
  };

  const getDomainOrderList = async (domainKey: string): Promise<Array<{ mvarName: string, itemAge: number, orderIndex: number }> | null> => {
    try {
      const globalStorage = getRuntimeStorage();
      if (!globalStorage) return null;
      
      const isPremium = appConfig.isPremium();
      const adminNodeName = mapDomainToAdminNodeName(domainKey, isPremium);
      
      console.log(`🎯 getDomainOrderList: Looking for stored AdminNodeDto for domain: ${domainKey} → AdminNode: ${adminNodeName} (Premium: ${isPremium})`);
      console.log(`🔍 DOMAIN CONSISTENCY CHECK:`);
      console.log(`  - Function parameter domainKey: ${domainKey}`);
      console.log(`  - Mapped AdminNode name: ${adminNodeName}`);
      console.log(`  - Premium mode: ${isPremium}`);
      
      // Look for the stored AdminNodeDto data using the correct key pattern
      const segmentId = isPremium ? '11270' : '11255';  // Premium vs Standard
      const adminNodesKey = `admin_nodes_segment_${segmentId}`;
      
      console.log(`🔍 Looking for stored AdminNodes at key: ${adminNodesKey}`);
      
      const storedNodesDataStr = await globalStorage.getItem(adminNodesKey);
      if (!storedNodesDataStr) {
        console.warn(`⚠️ No stored AdminNodes found at key: ${adminNodesKey}`);
        console.log(`� DEBUG: Checking alternative storage keys...`);
        
        // Check for alternative keys that might exist
        const alternativeKeys = [
          `admin_nodes_segment_11255`,
          `admin_nodes_segment_11270`,
          `admin_nodes_${segmentId}`,
          `admin_segment_${segmentId}`,
          'admin_nodes'
        ];
        
        for (const altKey of alternativeKeys) {
          const altData = await globalStorage.getItem(altKey);
          if (altData) {
            console.log(`🔍 DEBUG: Found alternative storage at key: ${altKey}`);
            console.log(`🔍 DEBUG: Alternative data size: ${altData.length} bytes`);
            
            try {
              const parsedData = JSON.parse(altData);
              console.log(`🔍 DEBUG: Alternative data keys: ${Object.keys(parsedData)}`);
            } catch (e) {
              console.log(`🔍 DEBUG: Alternative data is not valid JSON`);
            }
            break;
          }
        }
        
        console.log(`🔄 Falling back to AdminSegment-based domain order loading...`);
        return await loadDomainOrderFromAdminSegment(domainKey, isPremium, userStore, contentStore);
      }
      
      const storedNodesData = JSON.parse(storedNodesDataStr);
      
      if (!storedNodesData || typeof storedNodesData !== 'object') {
        console.warn(`⚠️ Invalid AdminNodes data format at key: ${adminNodesKey}`);
        console.log(`🔍 DEBUG: Data type: ${typeof storedNodesData}, data preview:`, storedNodesData ? Object.keys(storedNodesData).slice(0, 5) : 'null');
        console.log(`🔄 Falling back to AdminSegment-based domain order loading...`);
        return await loadDomainOrderFromAdminSegment(domainKey, isPremium, userStore, contentStore);
      }
      
      console.log(`🔍 DEBUG: Available stored AdminNodes:`, Object.keys(storedNodesData));
      
      // Find the specific domain node by AdminNode name
      const domainNodeData = storedNodesData[adminNodeName];
      if (!domainNodeData) {
        console.warn(`⚠️ No stored AdminNodeDto found for domain: ${adminNodeName}`);
        console.log(`🔍 Available stored domains:`, Object.keys(storedNodesData));
        console.log(`🔍 DEBUG: Checking for similar keys...`);
        
        // Check for similar keys that might match
        const similarKeys = Object.keys(storedNodesData).filter(key => 
          key.toLowerCase().includes(domainKey.toLowerCase()) ||
          key.toLowerCase().includes(adminNodeName.toLowerCase().replace('_', '').replace('x', ''))
        );
        
        if (similarKeys.length > 0) {
          console.log(`🔍 DEBUG: Found similar keys:`, similarKeys);
          // Try the first similar key
          const similarNodeData = storedNodesData[similarKeys[0]];
          if (similarNodeData && similarNodeData.Order && similarNodeData.Order.length > 0) {
            console.log(`✅ Using similar key ${similarKeys[0]} for domain ${domainKey}`);
            const orderData = similarNodeData.Order.map((orderItem: any, index: number) => ({
              mvarName: orderItem.Name,
              itemAge: parseFloat(orderItem.Val),
              orderIndex: index
            })).filter((item: { mvarName: string, itemAge: number, orderIndex: number }) => item.mvarName);
            
            console.log(`✅ Using similar stored AdminNodeDto order data: ${orderData.length} milestones`);
            return orderData;
          }
        }
        
        console.log(`🔄 Falling back to AdminSegment-based domain order loading...`);
        return await loadDomainOrderFromAdminSegment(domainKey, isPremium, userStore, contentStore);
      }
      
      if (!domainNodeData.Order || domainNodeData.Order.length === 0) {
        console.warn(`⚠️ No Order data found in stored AdminNodeDto for ${adminNodeName}`);
        console.log(`🔍 DEBUG: AdminNodeDto keys:`, Object.keys(domainNodeData));
        console.log(`🔄 Falling back to AdminSegment-based domain order loading...`);
        return await loadDomainOrderFromAdminSegment(domainKey, isPremium, userStore, contentStore);
      }
      
      // Convert stored AdminNodeDto.Order to the expected format
      // AdminNodeDto.Order uses 'Name' for milestone variable and 'Val' for age
      const orderData = domainNodeData.Order.map((orderItem: any, index: number) => {
        const mvarName = orderItem.Name;
        const itemAge = parseFloat(orderItem.Val);
        
        if (!mvarName) {
          console.warn(`⚠️ Could not find milestone name in order item:`, orderItem);
        }
        
        return {
          mvarName: mvarName,
          itemAge: itemAge,
          orderIndex: index
        };
      }).filter((item: { mvarName: string, itemAge: number, orderIndex: number }) => item.mvarName); // Filter out items without milestone names
      
      console.log(`✅ Using stored ${isPremium ? 'Premium' : 'Standard'} AdminNodeDto order data for ${adminNodeName}: ${orderData.length} milestones`);
      
      return orderData;
    } catch (error) {
      console.error('Error loading stored AdminNodeDto order data:', error);
      console.log(`🔄 Falling back to AdminSegment-based domain order loading due to error...`);
      return await loadDomainOrderFromAdminSegment(domainKey, appConfig.isPremium(), userStore, contentStore);
    }
  };

  // Load milestones function - now without automatic floor/ceiling recalculation
  const loadMilestones = async (recalculateFloorCeiling = false) => {
    try {
      if (!activeProfile || !selectedDomain) {
        setMilestones([]);
        setFloorCeilingInfo(null);
        return;
      }

      console.log(`🔍 DOMAIN DEBUG: Starting loadMilestones`);
      console.log(`  - selectedDomain.key: ${selectedDomain.key}`);
      console.log(`  - selectedDomain.name: ${selectedDomain.name || 'No name'}`);
      console.log(`  - isPremium: ${appConfig.isPremium()}`);
      console.log(`  - recalculateFloorCeiling: ${recalculateFloorCeiling}`);

      // Force fresh storage instance - always get the latest data
      console.log(`🔍 STORAGE REFRESH: Forcing fresh milestone storage instance`);
      sharedMilestoneStorage = null;
      const currentStorage = getSharedStorage();
      console.log(`🔍 STORAGE DEBUG: Using fresh milestone storage instance`);
      
      // Check if milestone data exists
      const masterData = await currentStorage.loadMasterData();
      console.log(`🔍 STORAGE DEBUG: Master data check`);
      console.log(`  - Total milestones in storage: ${masterData?.milestones?.length || 0}`);
      console.log(`  - Total texts in storage: ${masterData?.milestoneTexts?.length || 0}`);
      console.log(`  - Data version: ${masterData?.dataVersion || 'No version'}`);
      console.log(`  - Last sync: ${masterData?.lastSyncTimestamp || 'No sync time'}`);
      
      // If no milestones found, something is wrong with storage access
      if (!masterData || !masterData.milestones || masterData.milestones.length === 0) {
        console.error(`❌ CRITICAL: No milestone data found in storage after reload!`);
        console.log(`🔍 STORAGE TROUBLESHOOT: Checking storage keys directly...`);
        
        const globalStorage = getRuntimeStorage();
        if (globalStorage) {
          try {
            const masterDataStr = await globalStorage.getItem('MASTER_DATA_KEY');
            if (masterDataStr) {
              const directData = JSON.parse(masterDataStr);
              console.log(`🔍 DIRECT STORAGE: Found data directly with ${directData.milestones?.length || 0} milestones`);
            } else {
              console.log(`🔍 DIRECT STORAGE: No data found at MASTER_DATA_KEY`);
            }
          } catch (e) {
            console.error(`❌ DIRECT STORAGE ERROR:`, e);
          }
        }
        
        setMilestones([]);
        setFloorCeilingInfo(null);
        return;
      }
      
      // Check if domain PAS data exists
      const globalStorage = getRuntimeStorage();
      if (globalStorage) {
        const domainPasDataStr = await globalStorage.getItem('domain_pas_data');
        if (domainPasDataStr) {
          const domainPasData = JSON.parse(domainPasDataStr);
          console.log(`🔍 DOMAIN PAS DEBUG: Available domains in PAS data:`);
          Object.keys(domainPasData).forEach(domain => {
            const orderCount = domainPasData[domain]?.order?.length || 0;
            console.log(`  - ${domain}: ${orderCount} order items`);
          });
        } else {
          console.warn(`⚠️ No domain PAS data found in storage`);
        }
      }

      // Get the domain order list for current app tier
      const domainOrderList = await getDomainOrderList(selectedDomain.key);
      if (!domainOrderList || domainOrderList.length === 0) {
        console.warn(`⚠️ No domain order list found for ${selectedDomain.key} in ${appConfig.isPremium() ? 'Premium' : 'Standard'} mode`);
        setMilestones([]);
        setFloorCeilingInfo(null);
        return;
      }
      
      console.log(`🔍 DOMAIN ORDER DEBUG: Found ${domainOrderList.length} order items for ${selectedDomain.key}`);
      console.log(`  - First few order items:`, domainOrderList.slice(0, 3).map(item => `${item.mvarName} (${item.itemAge}mo)`));
      
      // Load milestones by mvar from domain order list
      const orderedMilestones: MilestoneWithText[] = [];
      
      console.log(`📋 Looking up ${domainOrderList.length} milestones by mvar for ${selectedDomain.key}`);
      
      // For each mvar in the domain order list, get the milestone by mvar
      for (const orderItem of domainOrderList) {
        const milestone = await currentStorage.getMilestoneByMvar(orderItem.mvarName, currentLanguage);
        
        if (milestone) {
          // Create milestone with correct itemage from domain order list
          const orderedMilestone: MilestoneWithText = {
            ...milestone,
            itemage: orderItem.itemAge  // Use itemage from domain order list
          };
          orderedMilestones.push(orderedMilestone);
          // console.log(`  ✅ Found milestone: ${orderItem.mvarName} (${orderItem.itemAge}mo)`);
        } else {
          console.warn(`  ⚠️ Milestone ${orderItem.mvarName} not found in storage`);
        }
      }
      
      // Sort by itemage (chronological order)
      orderedMilestones.sort((a, b) => (a.itemage || 0) - (b.itemage || 0));
      
      console.log(`🎯 MilestonesView: Loaded ${orderedMilestones.length} milestones for ${selectedDomain.key} using ${appConfig.isPremium() ? 'Premium' : 'Standard'} order list`);
      
      // CRITICAL DEBUG: Check domain key mapping
      const domainKey = mapDomainKey(selectedDomain.key, appConfig.isPremium());
      console.log(`🔍 DOMAIN MAPPING DEBUG:`);
      console.log(`  - Input selectedDomain.key: ${selectedDomain.key}`);
      console.log(`  - Input isPremium: ${appConfig.isPremium()}`);
      console.log(`  - Output domainKey: ${domainKey}`);
      
      // For premium users, validate the mapping is correct
      if (appConfig.isPremium()) {
        const expectedMappings = {
          'communication': 'communication',
          'fineMotor': 'finemotor',
          'grossMotor': 'grossmotor', 
          'cognitive': 'cognitive',
          'socialEmotional': 'socialemotional'
        };
        
        const expectedKey = expectedMappings[selectedDomain.key as keyof typeof expectedMappings];
        if (expectedKey && domainKey === expectedKey) {
          console.log(`✅ Domain mapping correct: ${selectedDomain.key} → ${domainKey}`);
        } else if (expectedKey) {
          console.error(`❌ DOMAIN MAPPING ERROR: Expected '${expectedKey}' but got '${domainKey}'`);
          console.log(`🔧 This will cause floor/ceiling calculation to use wrong domain!`);
        } else {
          console.warn(`⚠️ Unknown premium domain: ${selectedDomain.key}`);
        }
      } else {
        // For standard users, fine/gross motor should map to just 'motor'
        if ((selectedDomain.key === 'fineMotor' || selectedDomain.key === 'grossMotor') && domainKey === 'motor') {
          console.log(`✅ Standard user mapping correct: ${selectedDomain.key} → ${domainKey}`);
        } else {
          console.log(`✅ Standard domain mapping: ${selectedDomain.key} → ${domainKey}`);
        }
      }
      
      if (devshowall) {
        // For show all mode, load complete AdminSegment order and show from first non-null milestone forward
        console.log(`📋 Show all mode: Finding first non-null milestone in AdminSegment order`);
        
        const globalStorage = getRuntimeStorage();
        if (globalStorage) {
          try {
            const isPremium = appConfig.isPremium();
            const adminNodeName = mapDomainToAdminNodeName(domainKey, isPremium);
            const adminSegmentStorage = new AdminSegmentStorage(globalStorage);
            const adminNode = await adminSegmentStorage.getAdminNodeByDomain(adminNodeName);
            
            if (adminNode && adminNode.Order && adminNode.Order.length > 0) {
              console.log(`✅ Checking ${adminNode.Order.length} milestones in AdminSegment order for first non-null`);
              
              // Load all milestones from AdminSegment order
              const allOrderedMilestones: MilestoneWithText[] = [];
              
              for (let i = 0; i < adminNode.Order.length; i++) {
                const orderItem = adminNode.Order[i];
                const milestone = await milestoneStorage.getMilestoneByMvar(orderItem.Name, currentLanguage);
                if (milestone) {
                  allOrderedMilestones.push({
                    ...milestone,
                    itemage: parseFloat(orderItem.Val)
                  });
                } else {
                  // Mark position as null but keep tracking
                  allOrderedMilestones.push(null as any);
                }
              }
              
              // Find first non-null milestone using profile milestone states
              let firstNonNullIndex = -1;
              for (let i = 0; i < allOrderedMilestones.length; i++) {
                const milestone = allOrderedMilestones[i];
                if (milestone) {
                  // Check if this milestone has any user response
                  const milestoneState = activeProfile.milestonestate.find((ms: MilestoneState) => ms.mvarid === milestone.mvar);
                  if (milestoneState && milestoneState.state && milestoneState.state.trim() !== '') {
                    firstNonNullIndex = i;
                    console.log(`🎯 Found first non-null milestone at index ${i}, mvar: ${milestone.mvar}, state: ${milestoneState.state}`);
                    break;
                  }
                }
              }
              
              if (firstNonNullIndex === -1) {
                // No answered milestones found, use devFloor as starting point
                const devFloor = activeProfile.devfloor[domainKey] || 1;
                firstNonNullIndex = Math.max(0, devFloor - 1); // Convert to 0-based index
                console.log(`📍 No answered milestones found, using devFloor as starting point: index ${firstNonNullIndex}`);
              }
              
              // Show milestones from first non-null index to end of list
              const milestonesToShow = allOrderedMilestones.slice(firstNonNullIndex).filter(m => m !== null);
              milestonesToShow.sort((a, b) => (a.itemage || 0) - (b.itemage || 0));
              
              console.log(`📋 Showing ${milestonesToShow.length} milestones from index ${firstNonNullIndex} to end`);
              setMilestones(milestonesToShow);
            } else {
              // Fallback to domainOrderList if AdminSegment not available
              console.warn(`⚠️ AdminSegment order not available, using domain order list`);
              setMilestones(orderedMilestones);
            }
          } catch (error) {
            console.error('❌ Error loading AdminSegment order:', error);
            setMilestones(orderedMilestones);
          }
        } else {
          // No global storage available, use domain order list
          setMilestones(orderedMilestones);
        }
        
        // Clear floor/ceiling info since we're showing a custom subset
        setFloorCeilingInfo(null);
      } else {
        // if (recalculateFloorCeiling) {
          // Full calculation including floor/ceiling recalculation
          // getMilestoneRange handles the calculateDevFloorCeiling call internally
          const globalStorage = getRuntimeStorage();
          
          console.log(`🔍 FLOOR/CEILING DEBUG: About to call getMilestoneRange`);
          console.log(`  - domainKey parameter: ${domainKey}`);
          console.log(`  - selectedDomain.key: ${selectedDomain.key}`);
          console.log(`  - orderedMilestones count: ${orderedMilestones.length}`);
          console.log(`  - activeProfile.id: ${activeProfile.id}`);
          
          const result = await getMilestoneRange(activeProfile, domainKey, orderedMilestones, globalStorage);
          
          console.log(`🔍 FLOOR/CEILING RESULT:`);
          console.log(`  - floor: ${result.floor}`);
          console.log(`  - ceiling: ${result.ceiling}`);
          console.log(`  - profileUpdated: ${result.profileUpdated}`);
          console.log(`  - milestones returned: ${result.milestones.length}`);
          
          // Update profile if floor/ceiling values changed
          if (result.profileUpdated) {
            const updatedProfile = updateProfileDevFloorCeiling(
              activeProfile, 
              domainKey, 
              result.floor, 
              result.ceiling,
              result.devstate
            );
            updateProfile(activeProfile.id, {
              devfloor: updatedProfile.devfloor,
              devceiling: updatedProfile.devceiling,
              devstate: updatedProfile.devstate,
              lastActiveDate: updatedProfile.lastActiveDate
            });
          }
          
          // Sort result milestones by itemage (chronological order) - should already be sorted but ensure it
          const sortedResultMilestones = [...result.milestones].sort((a, b) => (a.itemage || 0) - (b.itemage || 0));
          
          setFloorCeilingInfo({ floor: result.floor, ceiling: result.ceiling });
          setMilestones(sortedResultMilestones);
        // } else {
        //   // Use existing floor/ceiling from profile, filter milestones accordingly
        //   const currentFloor = activeProfile.devfloor[domainKey] || 0;
        //   const currentCeiling = activeProfile.devceiling[domainKey] || orderedMilestones.length;
          
        //   console.log(`📊 Using existing floor/ceiling from profile: Floor=${currentFloor}, Ceiling=${currentCeiling}`);
          
        //   // Filter milestones based on existing floor/ceiling range
        //   const milestonesInRange = orderedMilestones.slice(currentFloor, currentCeiling + 1);
          
        //   setFloorCeilingInfo({ floor: currentFloor, ceiling: currentCeiling });
        //   setMilestones(milestonesInRange);
        // }
      }
    } catch (error) {
      console.error('Failed to load milestones:', error);
      setMilestones([]);
      setFloorCeilingInfo(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Navigation controls
  const toggleShowAll = () => {
    const newDevshowall = !devshowall;
    setDevshowall(newDevshowall);
    // No timing delays - auto-scroll will happen after milestone loading completes via useEffect
  };
  
  // Scroll to the devFloor milestone position
  const scrollToDevFloor = () => {
    if (!scrollViewRef.current || !floorCeilingInfo || milestones.length === 0) {
      return;
    }
    
    console.log(`🔍 DEBUG devFloor search:`);
    console.log(`   - Looking for devFloor: ${floorCeilingInfo.floor}`);
    console.log(`   - Milestones array length: ${milestones.length}`);
    console.log(`   - First few milestone seqnums: [${milestones.slice(0, 5).map(m => m.seqnum).join(', ')}]`);
    console.log(`   - Last few milestone seqnums: [${milestones.slice(-5).map(m => m.seqnum).join(', ')}]`);
    console.log(`   - DevFloor as array index (${floorCeilingInfo.floor - 1}): ${milestones[floorCeilingInfo.floor - 1]?.seqnum || 'undefined'}`);
    
    // DevFloor represents the position (1-based) in the AdminSegment order
    // Convert to 0-based index for the milestones array
    const devFloorIndex = floorCeilingInfo.floor - 1;
    
    if (devFloorIndex >= 0 && devFloorIndex < milestones.length) {
      // Calculate scroll position to show devFloor milestone at the TOP of the viewing area
      const cardHeight = 120;
      const scrollY = devFloorIndex * cardHeight;
      
      console.log(`📍 Positioning devFloor milestone at TOP of viewing area:`);
      console.log(`   - DevFloor index: ${devFloorIndex}, seqnum: ${milestones[devFloorIndex].seqnum}`);
      console.log(`   - ScrollY: ${scrollY} (milestone will appear at top of view)`);
      
      scrollViewRef.current.scrollTo({
        y: scrollY,
        animated: true
      });
    } else {
      console.log(`⚠️ DevFloor index ${devFloorIndex} is out of range for ${milestones.length} milestones`);
    }
  };
  
  const handleRefresh = async () => {
    try {
      setLoading(true);
      console.log('🔄 Refresh button clicked - recalculating developmental age and refreshing display');
      
      if (!activeProfile || !selectedDomain) {
        console.log('⚠️ Cannot refresh - no active profile or selected domain');
        setLoading(false);
        return;
      }
      
      // First, recalculate developmental age to update computation timestamp
      console.log('🧮 Step 1: Recalculating developmental age for current domain');
      await calculateDevelopmentalAgeOptimizedWithProfile(activeProfile, 'refresh-button');
      
      // Then reload milestones WITH floor/ceiling recalculation for complete refresh
      console.log('🔄 Step 2: Reloading milestones with floor/ceiling recalculation');
      loadMilestones(true);
      
      console.log('✅ Refresh complete - developmental age recalculated and display refreshed');
      
    } catch (error) {
      console.error('❌ Error during refresh:', error);
      setLoading(false);
    }
  };
  
  const handleUpdateConcerns = async () => {
    if (!activeProfile || !selectedDomain) return;
    // Attempt full recalculation when answers exist; the function catches its own errors.
    await calculateDevelopmentalAgeOptimizedWithProfile(activeProfile, 'update-concerns');
    // Always stamp the computation timestamp regardless of whether calculation succeeded
    // (e.g. domain has no answers yet — no calculation possible, but timestamp still needed).
    const domainKey = mapDomainKey(selectedDomain.key, appConfig.isPremium());
    const now = new Date().toISOString();
    updateProfile(activeProfile.id, {
      devresultcomputed: {
        ...activeProfile.devresultcomputed,
        [domainKey]: now,
      }
    });
    console.log(`✅ Update Concerns: timestamp stamped for ${domainKey}`);
    onUpdateConcerns?.();
  };

  const handlePrevDomain = async () => {
    if (selectedDomainIndex > 0) {
      try {
        console.log('◀️ Previous domain navigation - recalculating current domain before navigation');
        
        // First, recalculate developmental age for current domain to update timestamp
        if (activeProfile && selectedDomain) {
          await calculateDevelopmentalAgeOptimizedWithProfile(activeProfile, 'prev-navigation');
        }
        
        // Then navigate to previous domain
        console.log(`◀️ Navigating from domain ${selectedDomainIndex} to ${selectedDomainIndex - 1}`);
        setSelectedDomainIndex(selectedDomainIndex - 1);
        
      } catch (error) {
        console.error('❌ Error during previous domain navigation:', error);
        // Still navigate even if calculation fails
        setSelectedDomainIndex(selectedDomainIndex - 1);
      }
    }
  };
  
  const handleNextDomain = async () => {
    if (selectedDomainIndex < domains.length - 1) {
      try {
        console.log('▶️ Next domain navigation - recalculating current domain before navigation');
        
        // First, recalculate developmental age for current domain to update timestamp
        if (activeProfile && selectedDomain) {
          await calculateDevelopmentalAgeOptimizedWithProfile(activeProfile, 'next-navigation');
        }
        
        // Then navigate to next domain
        console.log(`▶️ Navigating from domain ${selectedDomainIndex} to ${selectedDomainIndex + 1}`);
        setSelectedDomainIndex(selectedDomainIndex + 1);
        
      } catch (error) {
        console.error('❌ Error during next domain navigation:', error);
        // Still navigate even if calculation fails
        setSelectedDomainIndex(selectedDomainIndex + 1);
      }
    }
  };

  // Initialize with simple setup - app startup should have loaded milestone data
  useEffect(() => {
    console.log('🔍 MILESTONE INIT: Simple initialization - app startup should have loaded milestone data');
    setIsInitialized(true);
  }, []); // Run once on mount - app initialization should have loaded milestone data
  
  // Load staleness threshold from config
  useEffect(() => {
    const loadThreshold = async () => {
      try {
        const globalStorage = getRuntimeStorage();
        if (globalStorage) {
          const threshold = await loadStalenessThreshold(globalStorage);
          setStalenessThreshold(threshold);
        }
      } catch (error) {
        console.warn('⚠️ Failed to load staleness threshold, using default:', error);
      }
    };
    loadThreshold();
  }, []);
  
  // Load milestones when domain changes or on initial load
  useEffect(() => {
    if (selectedDomain && isInitialized && activeProfile) {
      setLoading(true);
      // Reload milestones WITH floor/ceiling recalculation for domain changes
      // This also handles developmental age calculation internally
      loadMilestones(true);
    }
  }, [selectedDomainIndex, isInitialized, isPremium]);

  // Load milestones when devshowall changes 
  useEffect(() => {
    if (selectedDomain && isInitialized && activeProfile) {
      setLoading(true);
      // When switching to show all, load the subset from first non-null milestone forward
      loadMilestones(false);
    }
  }, [devshowall]);

  // Listen for storage invalidation signals from Development Tools
  useEffect(() => {
    const checkForInvalidation = () => {
      const currentInvalidationTime = (globalThis as any).__milestoneStorageInvalidated || 0;
      const lastCheckedTime = (globalThis as any).__milestoneLastChecked || 0;
      
      // Only react to genuine invalidation changes and ensure we don't create infinite loops
      if (currentInvalidationTime > lastCheckedTime && currentInvalidationTime !== lastInvalidationTime && selectedDomain && isInitialized && activeProfile) {
        console.log('🔄 External storage change detected, refreshing MilestonesView...');
        (globalThis as any).__milestoneLastChecked = currentInvalidationTime;
        
        // Force complete storage refresh by clearing the cached instance
        console.log('🔄 Forcing milestone storage refresh after invalidation...');
        sharedMilestoneStorage = null;
        
        // Immediately reload milestones with fresh storage (this will trigger getMilestoneStorage)
        console.log('🔄 Reloading milestones immediately after storage invalidation...');
        loadMilestones(false);
      }
    };

    // Check immediately and then periodically
    checkForInvalidation();
    const refreshInterval = setInterval(checkForInvalidation, 1000);

    return () => clearInterval(refreshInterval);
  }, [selectedDomain, isInitialized, activeProfile]);

  return (
    <View style={[styles.backgroundContainer, { backgroundColor: appConfig.getAppBackgroundColor() }]}>
      {/* Fixed Domain Header with Navigation Controls - full width background, constrained content */}
      <View style={styles.fixedDomainHeader}>
        <View style={{ maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }}>
        {/* First Row: Domain name and Developmental Age Score */}
        <View style={styles.domainTitleRow}>
          <Text style={styles.domainTitle}>
            {selectedDomain?.icon} {selectedDomain?.name} Milestones
          </Text>
          {showBackToSnapshot && onBackToSnapshot ? (
            <TouchableOpacity style={styles.backToSnapshotBtn} onPress={onBackToSnapshot}>
              <Text style={styles.backToSnapshotBtnText}>Back to Snapshot ▶</Text>
            </TouchableOpacity>
          ) : selectedDomainIndex === domains.length - 1 ? (
            <TouchableOpacity style={styles.backToSnapshotBtn} onPress={handleUpdateConcerns}>
              <Text style={styles.backToSnapshotBtnText}>Update Concerns ▶</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        
        {/* Second Row: Navigation Controls */}
        <View style={styles.navigationRow}>
          {/* Empty spacer mirrors right-side width to keep center truly centred */}
          <View style={styles.leftNavigationControls} />
          
          {/* Center Warning or Last Update Display */}
          {currentDomainNeedsUpdate && (
            <View style={styles.centerWarning}>
              <Text style={styles.centerHeaderText}>
                {activeProfile ? `${activeProfile.name}'s` : ''} {selectedDomain?.name} probable age is{' '}
                {(() => {
                  const currentDevAge = getCurrentDomainDevAge();
                  if (currentDevAge !== null) return `${currentDevAge.toFixed(1)} months`;
                  if (devAgeResults?.domainResults) return `${devAgeResults.domainResults.scoreValue?.toFixed(1) || 'N/A'} months`;
                  if (devAgeResults) return `${devAgeResults.developmentalAge.toFixed(1)} months`;
                  return 'unknown';
                })()}
              </Text>
              <Text style={styles.stalenessWarningText}>⚠️ Milestones need updating</Text>
            </View>
          )}
          {!currentDomainNeedsUpdate && showLastUpdate && (() => {
            const timestamp = getCurrentDomainTimestamp();
            if (timestamp) {
              return (
                <View style={styles.centerWarning}>
                  <Text style={styles.centerHeaderText}>
                    {activeProfile ? `${activeProfile.name}'s` : ''} {selectedDomain?.name} probable age is{' '}
                    {(() => {
                      const currentDevAge = getCurrentDomainDevAge();
                      if (currentDevAge !== null) return `${currentDevAge.toFixed(1)} months`;
                      if (devAgeResults?.domainResults) return `${devAgeResults.domainResults.scoreValue?.toFixed(1) || 'N/A'} months`;
                      if (devAgeResults) return `${devAgeResults.developmentalAge.toFixed(1)} months`;
                      return 'unknown';
                    })()}
                  </Text>
                  <Text style={styles.lastUpdateText}>
                    Last update: {formatComputationTimestamp(timestamp)}
                  </Text>
                </View>
              );
            }
            return null;
          })()}
          
          <View style={styles.rightNavigationControls}>
            <TouchableOpacity
              style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }]}
              onPress={toggleShowAll}
            >
              <Text style={[styles.navButtonText, { fontSize: 20 }]}>{devshowall ? '▼' : '▲'}</Text>
            </TouchableOpacity>

            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity 
                style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedDomainIndex === 0 && styles.navButtonDisabled]} 
                onPress={handlePrevDomain}
                disabled={selectedDomainIndex === 0}
              >
                <Text style={[styles.navButtonText, { fontSize: 20 }]}>◀</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedDomainIndex === domains.length - 1 && styles.navButtonDisabled]} 
                onPress={handleNextDomain}
                disabled={selectedDomainIndex === domains.length - 1}
              >
                <Text style={[styles.navButtonText, { fontSize: 20 }]}>▶</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
        </View>
      </View>

      {/* Domain Tab Menu - full width background, content constrained to headerWidthMax */}
      <View style={styles.tabSectionOuter}>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          style={[styles.tabContainer, { maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }]}
          contentContainerStyle={styles.tabContent}
        >
        {domains.map((domain, index) => (
          <TouchableOpacity
            key={domain.key}
            style={[
              styles.tab,
              selectedDomainIndex === index && { backgroundColor: appConfig.getNavButtonColor() }
            ]}
            onPress={() => setSelectedDomainIndex(index)}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
              <Text style={styles.tabIcon}>{domain.icon}</Text>
              <Text style={[
                styles.tabText,
                selectedDomainIndex === index && styles.selectedTabText
              ]}>
                {domain.name}
              </Text>
            </View>
          </TouchableOpacity>
        ))}
        </ScrollView>
      </View>

      {!activeProfile && (
        <View style={styles.noProfileWarning}>
          <Text style={styles.noProfileText}>
            No active profile selected. Please add or select a child profile to view milestones.
          </Text>
        </View>
      )}
      
      {/* Main Content Card */}
      <View style={styles.cardWrapper}>
        <View style={[styles.container, { maxWidth: appConfig.getSingleCardMaxWidth(), width: '100%' }]}>
      
      {/* Scrollable Content Area */}
      <ScrollView ref={scrollViewRef} style={styles.contentContainer}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading milestones...</Text>
          </View>
        ) : floorProgress ? (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>🔄 Calculating milestone window...</Text>
          </View>
        ) : (
          <>
            {milestones.length > 0 ? (
              milestones.map((milestone) => (
                <View key={milestone.mvar} style={[
                  styles.milestoneCard,
                  { backgroundColor: getMilestoneContainerBackgroundColor(milestone.mvar) }
                ]}>
                  <View style={{ flexDirection: 'row', alignItems: 'flex-start', flex: 1 }}>
                    <Text style={[styles.milestoneTitle, { marginRight: 0 }]}>
                      {debugInfoEnabled && milestone.mvar ? `${milestone.mvar}: ` : ''}
                    </Text>
                    <Text style={{ fontSize: 14, color: '#FFD700', marginRight: 6, marginTop: 2 }}>⭐</Text>
                    <Text style={[styles.milestoneTitle, { flex: 1, flexWrap: 'wrap' }]}>
                      {milestone.text || `Milestone ${milestone.seqnum}`}{debugInfoEnabled && milestone.itemage ? ` (${milestone.itemage}mo)` : ''}
                    </Text>
                  </View>
                  
                  {/* Milestone Answer Controls - Only show when not in show all mode */}
                  {!devshowall && (
                    <View style={styles.answerContainer}>
                      <View style={styles.answerButtons}>
                        {(['yes', 'sometimes', 'not-yet'] as const).map((answer) => {
                          const currentAnswer = getMilestoneAnswer(milestone.mvar);
                          const isSelected = currentAnswer === answer;
                          return (
                            <TouchableOpacity
                              key={answer}
                              style={[
                                styles.answerButton,
                                {
                                  backgroundColor: getButtonBackgroundColor(answer, isSelected),
                                  borderColor: isSelected ? getButtonBackgroundColor(answer, isSelected) : '#e1e8ed',
                                }
                              ]}
                              onPress={() => updateMilestoneAnswer(milestone.mvar, answer)}
                            >
                              <Text style={[
                                styles.answerButtonText,
                                isSelected && styles.answerButtonTextSelected
                              ]}>
                                {answer === 'yes' ? 'Yes' : answer === 'sometimes' ? 'Sometimes' : 'Not Yet'}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    </View>
                  )}
                </View>
              ))
            ) : (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>
                  No milestones available for {selectedDomain?.name}
                </Text>
                <Text style={styles.emptySubtext}>
                  {!isInitialized ? 'Initializing milestone data...' : 'Try a different domain or check your data source'}
                </Text>
              </View>
            )}
          </>
        )}
      </ScrollView>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  cardWrapper: {
    flex: 1,
    paddingHorizontal: appConfig.getCardMargin(),
    paddingTop: 12,
    alignItems: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  header: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  subtitle: {
    fontSize: 16,
    color: '#7f8c8d',
    textAlign: 'center',
  },
  tabSectionOuter: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
  },
  tabContainer: {
    maxHeight: 60,
  },
  tabContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexGrow: 1,
    justifyContent: 'center',
  },
  tab: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#f1f3f4',
    minWidth: 100,
    alignItems: 'center',
  },
  selectedTab: {
    backgroundColor: '#3498db',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5f6368',
  },
  tabIcon: {
    fontSize: 15,
  },
  selectedTabText: {
    color: '#ffffff',
  },
  contentContainer: {
    flex: 1,
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    fontSize: 16,
    color: '#7f8c8d',
  },
  fixedDomainHeader: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  domainTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  domainTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  backToSnapshotBtn: {
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 12,
    flexShrink: 0,
  },
  backToSnapshotBtnText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  devAgeInlineText: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  navigationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftNavigationControls: {
    width: 130, // mirrors rightNavigationControls total width: 36 + 14(gap) + (36+8+36)
  },
  rightNavigationControls: {
    flexDirection: 'row',
    gap: 14,
    alignItems: 'center',
  },
  centerWarning: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  stalenessWarningText: {
    fontSize: 13,
    color: '#e2e8f0',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  centerHeaderText: {
    fontSize: 13,
    color: '#e2e8f0',
    textAlign: 'center',
    marginBottom: 2,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  lastUpdateText: {
    fontSize: 13,
    color: '#e2e8f0',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  navButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#3498db',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  navButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.6,
  },
  navButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  milestoneCard: {
    backgroundColor: '#ffffff',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  milestoneTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  milestoneAge: {
    fontSize: 14,
    color: '#3498db',
    fontWeight: '500',
    marginBottom: 4,
  },
  answerContainer: {
    borderTopWidth: 1,
    borderTopColor: '#ecf0f1',
    paddingTop: 12,
  },
  answerButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  answerButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e1e8ed',
    alignItems: 'center',
  },
  answerButtonSelected: {
    backgroundColor: '#3498db',
    borderColor: '#3498db',
  },
  answerButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#5f6368',
  },
  answerButtonTextSelected: {
    color: '#ffffff',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    color: '#7f8c8d',
    textAlign: 'center',
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#bdc3c7',
    textAlign: 'center',
  },
  devAgeResults: {
    backgroundColor: '#f0f8ff',
    padding: 8,
    marginTop: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#3498db',
  },
  devAgeText: {
    fontSize: 13,
    color: '#2c3e50',
    fontWeight: '500',
    textAlign: 'center',
  },
  noProfileWarning: {
    backgroundColor: '#fff3cd',
    padding: 16,
    marginHorizontal: 16,
    marginBottom: 8,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#ffc107',
  },
  noProfileText: {
    fontSize: 14,
    color: '#856404',
    textAlign: 'center',
  },
});
